import React, { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { ELang, ELogLevel, EProductCode, EProductStatus, ESalesChannel, ESocketEvent } from 'shared/enum';
import { regProductCode } from 'shared/utils/helpers';
import { redirect } from 'shared/utils/redirect';

import { MAIN_PATH } from '../../../routes/consts';
import { API } from '../../api/loan';
import { restClient } from '../../api/rest';
import Loader from '../../components/Loader';
import { EPageStep, PROCESS_ID, processEventToPageMatcher, STEPS } from '../../consts';
import { useQuery } from '../../hooks/useQuery';
import { ScoringResponse } from '../../model/scoring';
import { logger } from '../../utils/logger';
import { getCurrentLang } from '../../utils/translation';
import { LoadingProvider } from '../loading';

import { useSocketState } from './hooks/socket';
import { GeneralInfoContext } from './GeneralInfoContext';
import { IGeneralInfo } from './models';
import { redirectTo } from './utils';

interface DtrumWindow extends Window {
  dtrum?: {
    reportCustomError?: (error: string | Error, customData?: Record<string, unknown>) => void;
  };
}

/** With GeneralInfo context */
export const WithGeneralInfoComponent: React.FC = ({ children }) => {
  let customErrorTimer: ReturnType<typeof setTimeout>;
  const reportCustomError = (window as DtrumWindow).dtrum?.reportCustomError;
  const query = useQuery();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { lang, productCode } = useParams();
  const firstPageUrl = `/${lang}/order/${productCode}${search}`;
  const isFirstPage = !!useMatch(MAIN_PATH);

  const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);
  const [processId, setProcessId] = useState(isFirstPage ? null : localStorage.getItem(PROCESS_ID));
  const [processStep, setProcessStep] = useState<ESocketEvent>();

  useSocketState({
    setProcessStep,
    setProcessId,
    processId,
  });

  const providedChannelType = query.get('t') as ESalesChannel;
  const channelType = Object.values(ESalesChannel).includes(providedChannelType) ? providedChannelType : ESalesChannel.BIRBANK_AZ;
  const providedLanguage = lang as ELang;
  const currentLang = Object.values(ELang).includes(providedLanguage) ? providedLanguage : getCurrentLang();
  const providedProduct = productCode as EProductCode;
  const isProductCode = regProductCode(providedProduct);

  const rest = restClient(currentLang, () => {
    navigate(`/${lang}/order/${productCode}/end`);
  });

  const currentUrlStep = useMemo(() => {
    const url = Object.keys(STEPS).find(p => !!matchPath(p, pathname));
    return url ? STEPS[url] : undefined;
  }, [pathname]);

  const isValidPage = useMemo(() => {
    return (
      currentUrlStep === EPageStep.FINAL ||
      (processStep !== ESocketEvent.REDIRECTION && !!currentUrlStep && processEventToPageMatcher(processStep).includes(currentUrlStep))
    );
  }, [currentUrlStep, processStep]);

  const processStepInitialLoading = useMemo(() => {
    return !processStep && processId;
  }, [processId, processStep]);

  useEffect(() => {
    if (!isProductCode) {
      redirect(channelType === ESalesChannel.BIRBANK_AZ ? 'https://birbank.az/cards/creditcards' : 'https://www.kapitalbank.az/cards/TaksitCards');
    }
  }, [productCode]);

  useEffect(() => {
    if (isFirstPage) {
      setNavigateToFirstPage(false);
      localStorage.clear();
      setProcessId(null);
      setProcessStep(undefined);
    }
  }, [pathname]);

  useEffect(() => {
    if (currentUrlStep !== EPageStep.STEP_LOADING || !processId) {
      return;
    }

    rest.get<ScoringResponse>(API.loan.getOrderDetail(processId)).then(({ data: { progressingStatus, step: stepFromBE } }) => {
      let resolvedStep = stepFromBE;

      if (progressingStatus === EProductStatus.ERROR) {
        resolvedStep = ESocketEvent.ERROR;
      } else if (progressingStatus === EProductStatus.END || progressingStatus === EProductStatus.COMPLETED) {
        resolvedStep = ESocketEvent.END;
      }

      if (resolvedStep && Object.values(ESocketEvent).includes(resolvedStep)) {
        setProcessStep(resolvedStep);
      }
    });
  }, [currentUrlStep, processId, rest]);

  useEffect(() => {
    if (!currentUrlStep) {
      logger(`No such step for url: ${pathname}`, ELogLevel.WARN);
      setNavigateToFirstPage(true);
    } else if (!processStepInitialLoading && !isValidPage) {
      if (processId && processStep) {
        redirectTo(processStep, {
          lang: currentLang,
          channelType,
          processId,
          productCode: providedProduct,
          navigate,
          rest,
        });
      } else {
        setNavigateToFirstPage(true);
      }
    }
  }, [currentUrlStep, processStep]);

  useEffect(() => {
    if (navigateToFirstPage) {
      navigate(firstPageUrl);
    }
  }, [navigateToFirstPage]);

  /** Dynatrace error logging. */
  useEffect(() => {
    if (reportCustomError) {
      if (!processStepInitialLoading && !isValidPage) {
        customErrorTimer = setTimeout(() => reportCustomError('PageNotValidForTooLong', { processId, processStep }), 10000);
      } else {
        clearTimeout(customErrorTimer);
      }
    }
    return () => {
      clearTimeout(customErrorTimer);
    };
  }, [isValidPage]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const normalizedPathname = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;

    if (isFirstPage && (params.has('t') || pathname !== normalizedPathname)) {
      navigate(normalizedPathname, { replace: true });
    }
  }, [search, pathname, isFirstPage, navigate]);

  const contextProps = useMemo<IGeneralInfo>(
    () => ({
      channelType,
      lang: currentLang,
      rest,
      productCode: providedProduct,
      setProcessStep,
      processId,
      setProcessId,
    }),
    [channelType, currentLang, rest, providedProduct, setProcessStep, processId, setProcessId],
  );

  return (
    <GeneralInfoContext.Provider value={contextProps}>
      <LoadingProvider>{!isValidPage ? <Loader /> : children}</LoadingProvider>
    </GeneralInfoContext.Provider>
  );
};
WithGeneralInfoComponent.displayName = 'WithGeneralInfo';

export { WithGeneralInfoComponent as WithGeneralInfo };
